import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Tag, ChevronLeft } from 'lucide-react';
import { blogPosts } from '../data/mock';
import { PageSEO } from '../components/PageSEO';

const BlogCard = ({ post, onSelect }) => (
  <article
    className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
    onClick={() => onSelect(post)}
    data-testid={`blog-card-${post.id}`}
  >
    <div className="overflow-hidden h-52">
      <img
        src={post.image}
        alt={post.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        loading="lazy"
      />
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full">
          {post.category}
        </span>
        <span className="flex items-center text-gray-400 text-xs gap-1">
          <Clock size={12} />
          {post.readTime}
        </span>
      </div>
      <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
        {post.title}
      </h2>
      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{post.date}</span>
        <span className="flex items-center text-emerald-600 text-sm font-semibold gap-1 group-hover:gap-2 transition-all">
          Leer más <ArrowRight size={14} />
        </span>
      </div>
    </div>
  </article>
);

const BlogArticle = ({ post, onBack }) => (
  <article className="max-w-3xl mx-auto" data-testid={`blog-article-${post.id}`}>
    <button
      onClick={onBack}
      className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium mb-8 transition-colors"
      data-testid="blog-back-button"
    >
      <ChevronLeft size={18} />
      Volver al Blog
    </button>

    <div className="mb-6 flex items-center gap-3">
      <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full">
        {post.category}
      </span>
      <span className="text-gray-400 text-sm">{post.date}</span>
      <span className="flex items-center text-gray-400 text-sm gap-1">
        <Clock size={14} />
        {post.readTime}
      </span>
    </div>

    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
      {post.title}
    </h1>

    <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
      <img
        src={post.image}
        alt={post.title}
        className="w-full h-64 sm:h-80 object-cover"
      />
    </div>

    <div className="prose prose-lg max-w-none">
      {post.content.map((paragraph, idx) => (
        <p key={idx} className="text-gray-700 leading-relaxed mb-5 text-base">
          {paragraph}
        </p>
      ))}
    </div>

    <div className="mt-10 pt-6 border-t border-gray-200">
      <div className="flex items-center gap-2 flex-wrap">
        <Tag size={16} className="text-gray-400" />
        {post.keywords.split(', ').map((kw, idx) => (
          <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
            {kw}
          </span>
        ))}
      </div>
    </div>

    {/* CTA */}
    <div className="mt-10 p-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl text-center text-white">
      <h3 className="text-2xl font-bold mb-3">¿Buscas un distribuidor para tu marca?</h3>
      <p className="mb-5 opacity-90">Contáctanos y descubre cómo podemos llevar tus productos a miles de puntos de venta</p>
      <Link
        to="/contacto"
        className="inline-flex items-center gap-2 bg-white text-emerald-700 px-6 py-3 rounded-full font-semibold hover:bg-emerald-50 transition-colors"
      >
        Contáctanos <ArrowRight size={16} />
      </Link>
    </div>
  </article>
);

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <PageSEO path="/blog" />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Blog Grupo BG
          </h1>
          <p className="text-lg text-emerald-100 max-w-2xl mx-auto">
            Artículos sobre distribución, logística y el mercado de consumo masivo en Ecuador
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {selectedPost ? (
            <BlogArticle post={selectedPost} onBack={() => setSelectedPost(null)} />
          ) : (
            <>
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Últimos Artículos</h2>
                <p className="text-gray-600">Información útil para marcas y empresas que buscan distribución en Ecuador</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-grid">
                {blogPosts.map(post => (
                  <BlogCard key={post.id} post={post} onSelect={setSelectedPost} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* SEO FAQ Section */}
      {!selectedPost && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Preguntas Frecuentes sobre Distribución</h2>
            <div className="space-y-6">
              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 flex justify-between items-center">
                  ¿Qué es un distribuidor de consumo masivo?
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Un distribuidor de consumo masivo es una empresa que se encarga de llevar productos desde los fabricantes hasta los puntos de venta (tiendas, supermercados, bazares). Ofrece logística, almacenamiento, fuerza de ventas y cobertura geográfica para asegurar que los productos estén disponibles donde los consumidores los necesitan.
                </p>
              </details>
              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 flex justify-between items-center">
                  ¿En qué provincias opera Grupo BG?
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Grupo BG opera en 6 provincias de la zona centro del Ecuador: Chimborazo, Bolívar, Morona Santiago, Tungurahua, Pastaza y Napo. Contamos con bodegas en Riobamba, Guaranda, Macas y Ambato para garantizar cobertura efectiva en toda la región.
                </p>
              </details>
              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 flex justify-between items-center">
                  ¿Cómo puedo distribuir mis productos con Grupo BG?
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Para distribuir tus productos con nosotros, contáctanos a través del formulario en nuestra página de contacto, vía WhatsApp al 0983112804 o al email contacto@grupobg.com.ec. Nuestro equipo evaluará tu producto y te presentará una propuesta de distribución adaptada a tus necesidades.
                </p>
              </details>
              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
                <summary className="font-semibold text-gray-900 flex justify-between items-center">
                  ¿Cuántos puntos de venta cubre Grupo BG?
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Actualmente atendemos más de 6,000 clientes activos en 6 provincias, incluyendo tiendas de barrio, minimarkets, supermercados, bazares y canales especializados. Nuestra fuerza de ventas visita cada punto de venta de forma regular para asegurar la disponibilidad de productos.
                </p>
              </details>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Blog;
